import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import CreateVehicleWizard from '@/components/partners/CreateVehicleWizard';
import PartnerLoginSheet from '@/components/partners/PartnerLoginSheet';
import { cn } from '@/lib/utils';
import {
    Settings, Pencil, Car, ChevronRight, Search,
    Plus, Check, X, Loader2, Unlink, KeyRound,
} from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import { fetchPartnerSheetVehicles } from '@/components/partners/partnerReads';
import { toast } from 'sonner';

/**
 * `partners.address` reaches `integrations/supabase/types.ts` only when the types are regenerated
 * against PRODUCTION, after the backend promote — the hatch `lib/agreements/api.ts:4-7` describes.
 */
const db = supabase as unknown as SupabaseClient;

// ─── Types ───────────────────────────────────────────────────────────────────

interface ManagePartnerSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    partnerId: number;
    partnerName: string;
    /** `partners.address` — printed on every Vehicle Schedule this partner signs [D-B48]. */
    partnerAddress: string | null;
}

interface UnassignedVehicle {
    id: number;
    make: string;
    model: string;
    color: string;
    vin: string;
    registration_number: string | null;
}

// ─── ActionCard ──────────────────────────────────────────────────────────────

interface ActionCardProps {
    icon: any;
    title: string;
    description: string;
    onClick: () => void;
    colorClass: string;
}

const ActionCard = ({ icon: Icon, title, description, onClick, colorClass }: ActionCardProps) => (
    <button
        onClick={onClick}
        className="w-full text-left group relative transition-all duration-300 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 hover:scale-[1.01]"
    >
        <Card className="h-full border border-border shadow-sm transition-all duration-300 group-hover:shadow-md group-hover:border-primary/20">
            <div className={cn(
                "absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity duration-500",
                colorClass.replace('text-', 'bg-')
            )} />
            <CardContent className="p-3 flex items-center gap-3">
                <div className={cn(
                    "p-2 rounded-lg transition-all duration-300 shadow-sm shrink-0",
                    "bg-white dark:bg-muted border border-muted",
                    "group-hover:scale-105",
                    colorClass
                )}>
                    <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1 space-y-1">
                    <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-foreground tracking-tight">{title}</h3>
                        <ChevronRight className="h-4 w-4 text-muted-foreground/50 group-hover:text-primary group-hover:translate-x-1 transition-all" />
                    </div>
                    <p className="text-sm text-muted-foreground leading-snug">{description}</p>
                </div>
            </CardContent>
        </Card>
    </button>
);

// ─── Edit Partner Sheet ──────────────────────────────────────────────────────

function EditPartnerSheet({
    open,
    onOpenChange,
    partnerId,
    partnerName,
    partnerAddress,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    partnerId: number;
    partnerName: string;
    partnerAddress: string | null;
}) {
    const queryClient = useQueryClient();
    const [name, setName] = useState(partnerName);
    const [address, setAddress] = useState(partnerAddress ?? '');

    useEffect(() => {
        if (open) {
            setName(partnerName);
            setAddress(partnerAddress ?? '');
        }
    }, [open, partnerName, partnerAddress]);

    const updateMutation = useMutation({
        mutationFn: async () => {
            const { error } = await db
                .from('partners')
                .update({ name: name.trim(), address: address.trim() || null })
                .eq('id', partnerId);
            if (error) throw error;
        },
        onSuccess: () => {
            toast.success('Partner updated');
            queryClient.invalidateQueries({ queryKey: ['partner'] });
            queryClient.invalidateQueries({ queryKey: ['partners'] });
            onOpenChange(false);
        },
        onError: (e: Error) => toast.error(e.message),
    });

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                className="!inset-y-4 !right-4 !h-auto !w-[90vw] sm:!w-[500px] !max-w-none rounded-2xl border shadow-2xl bg-background p-6 flex flex-col gap-4"
            >
                <SheetTitle className="text-2xl font-bold">Edit Partner</SheetTitle>
                <p className="text-muted-foreground text-sm">Update partner details.</p>

                <div className="space-y-2">
                    <Label htmlFor="partner-name">Partner Name *</Label>
                    <Input id="partner-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Partner name" autoFocus />
                </div>

                <div className="space-y-2">
                    <Label htmlFor="partner-address">Registered Address</Label>
                    <Textarea
                        id="partner-address"
                        autoComplete="off"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        placeholder="House number, street, town"
                        rows={3}
                    />
                    <p className="text-xs text-muted-foreground">
                        Printed on the Vehicle Schedule the partner signs, and there is nowhere else a
                        partner’s address is held.
                    </p>
                </div>

                <div className="pt-4 border-t flex justify-end gap-2">
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button
                        onClick={() => updateMutation.mutate()}
                        disabled={
                            !name.trim()
                            || (name.trim() === partnerName && address.trim() === (partnerAddress ?? '').trim())
                            || updateMutation.isPending
                        }
                    >
                        {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        Save Changes
                    </Button>
                </div>
            </SheetContent>
        </Sheet>
    );
}

// ─── Assign Vehicles Sheet ───────────────────────────────────────────────────

function AssignVehiclesSheet({
    open,
    onOpenChange,
    partnerId,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    partnerId: number;
}) {
    const queryClient = useQueryClient();
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 300);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [showCreateWizard, setShowCreateWizard] = useState(false);

    // Reset state when sheet opens
    useEffect(() => {
        if (open) {
            setSearch('');
            setSelectedIds([]);
        }
    }, [open]);

    // Fetch unassigned vehicles (partner_id IS NULL); a search runs on the server before the limit
    const { data: vehicles, isLoading } = useQuery({
        queryKey: ['unassigned-vehicles-for-partner', debouncedSearch],
        queryFn: (): Promise<UnassignedVehicle[]> => fetchPartnerSheetVehicles({ unassigned: true }, debouncedSearch),
        enabled: open,
    });

    const toggleVehicle = (id: number) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]
        );
    };

    // Assign mutation
    const assignMutation = useMutation({
        mutationFn: async () => {
            const { error } = await supabase
                .from('vehicles')
                .update({ partner_id: partnerId })
                .in('id', selectedIds);
            if (error) throw error;
        },
        onSuccess: () => {
            toast.success(`${selectedIds.length} vehicle${selectedIds.length > 1 ? 's' : ''} assigned`);
            queryClient.invalidateQueries({ queryKey: ['partner'] });
            queryClient.invalidateQueries({ queryKey: ['partner-vehicles'] });
            queryClient.invalidateQueries({ queryKey: ['partner-vehicles-page'] });
            queryClient.invalidateQueries({ queryKey: ['partner-vehicle-stats'] });
            queryClient.invalidateQueries({ queryKey: ['partner-financial-summary'] });
            queryClient.invalidateQueries({ queryKey: ['partner-recent-activity'] });
            queryClient.invalidateQueries({ queryKey: ['partners'] });
            onOpenChange(false);
        },
        onError: (e: Error) => toast.error(e.message),
    });

    // Build display for chips — include newly created vehicles not yet in the list
    const getVehicleLabel = (id: number) => {
        const v = vehicles?.find(v => v.id === id);
        if (v) return v.registration_number || `${v.make} ${v.model}`;
        return `Vehicle #${id}`;
    };

    return (
        <>
            <Sheet open={open} onOpenChange={onOpenChange}>
                <SheetContent
                    side="right"
                    className="!inset-y-4 !right-4 h-[calc(100vh-2rem)] !w-[90vw] sm:!w-[500px] !max-w-none rounded-2xl border shadow-2xl bg-background p-6 flex flex-col gap-0"
                >
                    <div className="flex-none space-y-1 mb-4">
                        <SheetTitle className="text-2xl font-bold">Assign Vehicles</SheetTitle>
                        <p className="text-muted-foreground text-sm">
                            Select vehicles to assign to this partner, or create a new one.
                        </p>
                    </div>

                    {/* Selected chips */}
                    {selectedIds.length > 0 && (
                        <div className="flex-none flex flex-wrap gap-1.5 mb-3">
                            {selectedIds.map(id => (
                                <Badge key={id} variant="secondary" className="gap-1 pl-2 pr-1 py-1">
                                    {getVehicleLabel(id)}
                                    <button onClick={() => toggleVehicle(id)} className="hover:bg-muted rounded-sm p-0.5">
                                        <X className="h-3 w-3" />
                                    </button>
                                </Badge>
                            ))}
                        </div>
                    )}

                    {/* Search */}
                    <div className="flex-none relative mb-3">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Search by make, model, registration..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-9"
                        />
                    </div>

                    {/* Vehicle list */}
                    <ScrollArea className="flex-1 min-h-0 -mx-1 px-1">
                        <div className="space-y-2 pb-2">
                            {isLoading ? (
                                [...Array(4)].map((_, i) => <Skeleton key={i} className="h-16" />)
                            ) : vehicles?.length === 0 ? (
                                <div className="text-center py-12 text-muted-foreground">
                                    <Car className="h-10 w-10 mx-auto mb-2 opacity-50" />
                                    <p className="text-sm font-medium">No unassigned vehicles found</p>
                                    <p className="text-xs mt-1">Create a new vehicle using the button below</p>
                                </div>
                            ) : (
                                vehicles?.map(v => {
                                    const isSelected = selectedIds.includes(v.id);
                                    return (
                                        <button
                                            key={v.id}
                                            onClick={() => toggleVehicle(v.id)}
                                            className={cn(
                                                "w-full text-left rounded-lg border p-3 transition-all hover:border-primary hover:bg-primary/5",
                                                isSelected
                                                    ? "border-primary bg-primary/5 ring-1 ring-primary"
                                                    : "border-border/60 bg-card"
                                            )}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className={cn(
                                                    "h-8 w-8 rounded-md flex items-center justify-center shrink-0 transition-colors",
                                                    isSelected ? "bg-primary text-primary-foreground" : "bg-muted"
                                                )}>
                                                    {isSelected ? <Check className="h-4 w-4" /> : <Car className="h-4 w-4 text-muted-foreground" />}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-medium text-sm">
                                                        {v.registration_number || v.vin}
                                                    </p>
                                                    <p className="text-xs text-muted-foreground truncate">
                                                        {v.make} {v.model} · {v.color}
                                                    </p>
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })
                            )}
                        </div>

                        {/* Create new vehicle button */}
                        <button
                            onClick={() => setShowCreateWizard(true)}
                            className="w-full mt-3 flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border/60 p-3 text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                        >
                            <Plus className="h-4 w-4" />
                            Create New Vehicle
                        </button>
                    </ScrollArea>

                    {/* Footer */}
                    <div className="flex-none pt-4 border-t flex justify-end gap-2 mt-2">
                        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                        <Button
                            onClick={() => assignMutation.mutate()}
                            disabled={selectedIds.length === 0 || assignMutation.isPending}
                        >
                            {assignMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Assign {selectedIds.length > 0 ? `${selectedIds.length} Vehicle${selectedIds.length > 1 ? 's' : ''}` : ''}
                        </Button>
                    </div>
                </SheetContent>
            </Sheet>

            {/* Create Vehicle Wizard */}
            <CreateVehicleWizard
                open={showCreateWizard}
                onOpenChange={setShowCreateWizard}
                onSuccess={(vehicleId) => {
                    setSelectedIds(prev => [...prev, vehicleId]);
                }}
            />
        </>
    );
}

// ─── Unassign Vehicles Sheet ─────────────────────────────────────────────────

interface PartnerVehicle {
    id: number;
    make: string;
    model: string;
    color: string;
    vin: string;
    registration_number: string | null;
}

function UnassignVehiclesSheet({
    open,
    onOpenChange,
    partnerId,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    partnerId: number;
}) {
    const queryClient = useQueryClient();
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 300);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);

    useEffect(() => {
        if (open) {
            setSearch('');
            setSelectedIds([]);
        }
    }, [open]);

    // Fetch partner's assigned vehicles; a search runs on the server before the limit
    const { data: vehicles, isLoading } = useQuery({
        queryKey: ['partner-assigned-vehicles', partnerId, debouncedSearch],
        queryFn: (): Promise<PartnerVehicle[]> => fetchPartnerSheetVehicles({ partnerId }, debouncedSearch),
        enabled: open,
    });

    const toggleVehicle = (id: number) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]
        );
    };

    const unassignMutation = useMutation({
        mutationFn: async () => {
            const { error } = await supabase
                .from('vehicles')
                .update({ partner_id: null })
                .in('id', selectedIds);
            if (error) throw error;
        },
        onSuccess: () => {
            toast.success(`${selectedIds.length} vehicle${selectedIds.length > 1 ? 's' : ''} unassigned`);
            queryClient.invalidateQueries({ queryKey: ['partner'] });
            queryClient.invalidateQueries({ queryKey: ['partner-vehicles'] });
            queryClient.invalidateQueries({ queryKey: ['partner-vehicles-page'] });
            queryClient.invalidateQueries({ queryKey: ['partner-vehicle-stats'] });
            queryClient.invalidateQueries({ queryKey: ['partner-financial-summary'] });
            queryClient.invalidateQueries({ queryKey: ['partner-recent-activity'] });
            queryClient.invalidateQueries({ queryKey: ['partner-assigned-vehicles'] });
            queryClient.invalidateQueries({ queryKey: ['partners'] });
            onOpenChange(false);
        },
        onError: (e: Error) => toast.error(e.message),
    });

    const getVehicleLabel = (id: number) => {
        const v = vehicles?.find(v => v.id === id);
        if (v) return v.registration_number || `${v.make} ${v.model}`;
        return `Vehicle #${id}`;
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                className="!inset-y-4 !right-4 h-[calc(100vh-2rem)] !w-[90vw] sm:!w-[500px] !max-w-none rounded-2xl border shadow-2xl bg-background p-6 flex flex-col gap-0"
            >
                <div className="flex-none space-y-1 mb-4">
                    <SheetTitle className="text-2xl font-bold">Unassign Vehicles</SheetTitle>
                    <p className="text-muted-foreground text-sm">
                        Select vehicles to remove from this partner.
                    </p>
                </div>

                {/* Selected chips */}
                {selectedIds.length > 0 && (
                    <div className="flex-none flex flex-wrap gap-1.5 mb-3">
                        {selectedIds.map(id => (
                            <Badge key={id} variant="secondary" className="gap-1 pl-2 pr-1 py-1">
                                {getVehicleLabel(id)}
                                <button onClick={() => toggleVehicle(id)} className="hover:bg-muted rounded-sm p-0.5">
                                    <X className="h-3 w-3" />
                                </button>
                            </Badge>
                        ))}
                    </div>
                )}

                {/* Search */}
                <div className="flex-none relative mb-3">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        placeholder="Search by make, model, registration..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9"
                    />
                </div>

                {/* Vehicle list */}
                <ScrollArea className="flex-1 min-h-0 -mx-1 px-1">
                    <div className="space-y-2 pb-2">
                        {isLoading ? (
                            [...Array(4)].map((_, i) => <Skeleton key={i} className="h-16" />)
                        ) : vehicles?.length === 0 ? (
                            <div className="text-center py-12 text-muted-foreground">
                                <Car className="h-10 w-10 mx-auto mb-2 opacity-50" />
                                <p className="text-sm font-medium">No vehicles assigned</p>
                                <p className="text-xs mt-1">This partner has no vehicles to unassign</p>
                            </div>
                        ) : (
                            vehicles?.map(v => {
                                const isSelected = selectedIds.includes(v.id);
                                return (
                                    <button
                                        key={v.id}
                                        onClick={() => toggleVehicle(v.id)}
                                        className={cn(
                                            "w-full text-left rounded-lg border p-3 transition-all hover:border-destructive/50 hover:bg-destructive/5",
                                            isSelected
                                                ? "border-destructive bg-destructive/5 ring-1 ring-destructive"
                                                : "border-border/60 bg-card"
                                        )}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={cn(
                                                "h-8 w-8 rounded-md flex items-center justify-center shrink-0 transition-colors",
                                                isSelected ? "bg-destructive text-destructive-foreground" : "bg-muted"
                                            )}>
                                                {isSelected ? <Check className="h-4 w-4" /> : <Car className="h-4 w-4 text-muted-foreground" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-sm">
                                                    {v.registration_number || v.vin}
                                                </p>
                                                <p className="text-xs text-muted-foreground truncate">
                                                    {v.make} {v.model} · {v.color}
                                                </p>
                                            </div>
                                        </div>
                                    </button>
                                );
                            })
                        )}
                    </div>
                </ScrollArea>

                {/* Footer */}
                <div className="flex-none pt-4 border-t flex justify-end gap-2 mt-2">
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button
                        variant="destructive"
                        onClick={() => unassignMutation.mutate()}
                        disabled={selectedIds.length === 0 || unassignMutation.isPending}
                    >
                        {unassignMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        Unassign {selectedIds.length > 0 ? `${selectedIds.length} Vehicle${selectedIds.length > 1 ? 's' : ''}` : ''}
                    </Button>
                </div>
            </SheetContent>
        </Sheet>
    );
}

// ─── Main Manage Sheet (compact action picker) ───────────────────────────────

export default function ManagePartnerSheet({
    open,
    onOpenChange,
    partnerId,
    partnerName,
    partnerAddress,
}: ManagePartnerSheetProps) {
    const [editOpen, setEditOpen] = useState(false);
    const [assignOpen, setAssignOpen] = useState(false);
    const [unassignOpen, setUnassignOpen] = useState(false);
    const [loginOpen, setLoginOpen] = useState(false);

    return (
        <>
            {/* Compact action picker sheet */}
            <Sheet open={open} onOpenChange={onOpenChange}>
                <SheetContent
                    side="right"
                    className="!inset-y-4 !right-4 h-[calc(100vh-2rem)] !w-[90vw] sm:!w-[420px] !max-w-none rounded-2xl border shadow-2xl bg-background p-6"
                >
                    <div className="space-y-1.5 mb-4">
                        <div className="flex items-center gap-2 text-primary font-medium tracking-wide text-xs uppercase">
                            <Settings className="h-3.5 w-3.5" />
                            Management
                        </div>
                        <SheetTitle className="text-2xl font-bold">Manage Partner</SheetTitle>
                        <p className="text-muted-foreground text-sm">
                            Select an action to manage this partner.
                        </p>
                    </div>

                    <div className="grid gap-2">
                        <ActionCard
                            icon={Pencil}
                            title="Edit Partner"
                            description="Update the partner's name and details."
                            onClick={() => { onOpenChange(false); setEditOpen(true); }}
                            colorClass="text-blue-600"
                        />
                        <ActionCard
                            icon={Car}
                            title="Assign Vehicles"
                            description="Add vehicles to this partner."
                            onClick={() => { onOpenChange(false); setAssignOpen(true); }}
                            colorClass="text-emerald-600"
                        />
                        <ActionCard
                            icon={Unlink}
                            title="Unassign Vehicles"
                            description="Remove vehicles from this partner."
                            onClick={() => { onOpenChange(false); setUnassignOpen(true); }}
                            colorClass="text-orange-600"
                        />
                        <ActionCard
                            icon={KeyRound}
                            title="Partner Login"
                            description="Who signs in to the partner app and signs their schedules."
                            onClick={() => { onOpenChange(false); setLoginOpen(true); }}
                            colorClass="text-violet-600"
                        />
                    </div>
                </SheetContent>
            </Sheet>

            {/* Separate sheets for each action */}
            <EditPartnerSheet
                open={editOpen}
                onOpenChange={setEditOpen}
                partnerId={partnerId}
                partnerName={partnerName}
                partnerAddress={partnerAddress}
            />
            <AssignVehiclesSheet
                open={assignOpen}
                onOpenChange={setAssignOpen}
                partnerId={partnerId}
            />
            <UnassignVehiclesSheet
                open={unassignOpen}
                onOpenChange={setUnassignOpen}
                partnerId={partnerId}
            />
            <PartnerLoginSheet
                open={loginOpen}
                onOpenChange={setLoginOpen}
                partnerId={partnerId}
                partnerName={partnerName}
            />
        </>
    );
}
